import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isDemoUser, simulateDemoAction } from "@/lib/demoMode";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { DailyBriefCard } from "@/components/DailyBriefCard";
import { InsightsRecommendationsFeed } from "@/components/InsightsRecommendationsFeed";
import { ByTheWayNudge } from "@/components/ByTheWayNudge";
import { LessonCard } from "@/components/instructor-dashboard/LessonCard";
import { MorningReadinessCard } from "@/components/instructor-dashboard/MorningReadinessCard";
import { AssignedVehicleCard } from "@/components/instructor-dashboard/AssignedVehicleCard";
import { VehicleIssueDialog } from "@/components/instructor-dashboard/VehicleIssueDialog";
import { LessonCompleteDialog } from "@/components/instructor-dashboard/LessonCompleteDialog";
import { RunningLateDrawer } from "@/components/instructor-dashboard/RunningLateDrawer";
import { Clock, LogIn, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function InstructorDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueForm, setIssueForm] = useState({ vehicle_id: "", description: "", priority: "medium", photos: [] });
  const [notesDialog, setNotesDialog] = useState(null);
  const [notesText, setNotesText] = useState("");
  const [clockLoading, setClockLoading] = useState(false);
  const [lateTarget, setLateTarget] = useState(null);
  const [lateStep, setLateStep] = useState(1);
  const [lateReason, setLateReason] = useState("");
  const [lateDelay, setLateDelay] = useState("");
  const [lateSending, setLateSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/dashboard/instructor");
      setData(res.data);
    } catch {
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/vehicles").then((res) => setVehicles(res.data)).catch(() => {});
  }, [load]);

  const handleClock = async () => {
    setClockLoading(true);
    try {
      if (data.clocked_in) {
        await api.post("/time-entries/clock-out", {});
        toast.success("Clocked out — have a great rest of your day.");
      } else {
        await api.post("/time-entries/clock-in", {});
        toast.success("Clocked in — let's have a safe, productive day!");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setClockLoading(false);
    }
  };

  const markStatus = async (apptId, status) => {
    try {
      await api.put(`/appointments/${apptId}/status`, { status });
      toast.success(`Lesson marked as ${status.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update lesson");
    }
  };

  const saveNotes = async () => {
    try {
      await api.put(`/appointments/${notesDialog.id}/status`, { status: notesDialog.status || "completed", instructor_notes: notesText });
      toast.success("Lesson marked complete — nice work.");
      setNotesDialog(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save notes");
    }
  };

  const submitIssue = async () => {
    if (!issueForm.vehicle_id || !issueForm.description) {
      toast.error("Select a vehicle and describe the issue");
      return;
    }
    try {
      await api.post("/vehicle-issues", { ...issueForm, photos: issueForm.photos.map((p) => p.data) });
      toast.success("Vehicle issue reported — the office has been notified.");
      setIssueOpen(false);
      setIssueForm({ vehicle_id: "", description: "", priority: "medium", photos: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit issue");
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        setIssueForm((f) => ({ ...f, photos: [...f.photos, { uid, data: reader.result }] }));
      };
      reader.readAsDataURL(file);
    });
  };

  const openReportIssue = () => {
    if (data?.assigned_vehicle) {
      setIssueForm((f) => ({ ...f, vehicle_id: data.assigned_vehicle.id }));
    }
    setIssueOpen(true);
  };

  const openRunningLate = (appt) => {
    setLateTarget(appt);
    setLateStep(1);
    setLateReason("");
    setLateDelay("");
  };

  const openRunningLateForNext = () => {
    const target = data?.todays_schedule?.[0];
    if (!target) {
      toast.error("You have no lessons today to report as running late.");
      return;
    }
    openRunningLate(target);
  };

  const sendRunningLate = async () => {
    if (!lateReason || !lateDelay) return;
    if (isDemoUser(user)) {
      simulateDemoAction(
        `the office and ${lateTarget?.student_name || "the student"} would be notified instantly that you're running about ${lateDelay} minutes late (${lateReason}).`
      );
      setLateTarget(null);
      return;
    }
    setLateSending(true);
    try {
      await api.post(`/appointments/${lateTarget.id}/running-late`, { reason: lateReason, delay_minutes: parseInt(lateDelay, 10) });
      toast.success("Update sent — office and student notified. You're covered.");
      setLateTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send running late update");
    } finally {
      setLateSending(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-20 rounded-[var(--radius)]" />
        <Skeleton className="h-40 rounded-[var(--radius)]" />
        <Skeleton className="h-64 rounded-[var(--radius)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="instructor-dashboard-page">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm text-muted-foreground">Welcome back,</p>
          <p className="font-heading text-xl font-semibold">{data.instructor?.full_name}</p>
        </div>
        <Button
          onClick={handleClock}
          disabled={clockLoading}
          className={data.clocked_in ? "bg-destructive hover:bg-destructive/90" : "bg-[hsl(var(--role-accent))] hover:opacity-95"}
          data-testid={data.clocked_in ? "clock-out-button" : "clock-in-button"}
        >
          {data.clocked_in ? <LogOut className="h-4 w-4 mr-2" /> : <LogIn className="h-4 w-4 mr-2" />}
          {data.clocked_in ? "Clock Out" : "Clock In"}
        </Button>
      </div>

      <DailyBriefCard brief={data.daily_brief} />

      <ByTheWayNudge nudge={data.by_the_way} />

      <MorningReadinessCard
        data={data}
        onRunningLateForNext={openRunningLateForNext}
        onSubmitExpense={() => navigate("/expenses")}
      />

      {data.assigned_vehicle && (
        <AssignedVehicleCard vehicle={data.assigned_vehicle} onReportIssue={openReportIssue} />
      )}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold font-heading flex items-center gap-2">
          <Clock className="h-5 w-5 text-[hsl(var(--role-accent))]" /> Today&apos;s Schedule
        </h2>
        {data.todays_schedule.length === 0 ? (
          <div className="rounded-[var(--radius)] border bg-card p-8 text-center text-sm text-muted-foreground">
            No lessons scheduled for today. Enjoy the break!
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {data.todays_schedule.map((appt) => (
              <LessonCard key={appt.id} appt={appt} showActions onComplete={setNotesDialog} onMarkStatus={markStatus} onRunningLate={openRunningLate} />
            ))}
          </div>
        )}
      </section>

      <InsightsRecommendationsFeed items={data.insights} />

      <section className="space-y-3">
        <h2 className="text-lg font-semibold font-heading">Upcoming Lessons</h2>
        {data.upcoming_lessons.length === 0 ? (
          <div className="rounded-[var(--radius)] border bg-card p-8 text-center text-sm text-muted-foreground">
            No upcoming lessons yet.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {data.upcoming_lessons.map((appt) => (
              <LessonCard key={appt.id} appt={appt} onRunningLate={openRunningLate} />
            ))}
          </div>
        )}
      </section>

      <VehicleIssueDialog
        open={issueOpen}
        onOpenChange={setIssueOpen}
        vehicles={vehicles}
        form={issueForm}
        onFormChange={setIssueForm}
        onPhotoChange={handlePhotoChange}
        onSubmit={submitIssue}
      />

      <LessonCompleteDialog
        open={!!notesDialog}
        onOpenChange={(v) => !v && setNotesDialog(null)}
        notesText={notesText}
        onNotesChange={setNotesText}
        onSave={saveNotes}
      />

      <RunningLateDrawer
        open={!!lateTarget}
        onOpenChange={(v) => !v && setLateTarget(null)}
        step={lateStep}
        reason={lateReason}
        onReasonChange={setLateReason}
        delay={lateDelay}
        onDelayChange={setLateDelay}
        onBack={() => setLateStep(1)}
        onNext={() => setLateStep(2)}
        onSend={sendRunningLate}
        sending={lateSending}
      />
    </div>
  );
}
